import React, { useEffect } from "react";
import Select, { components } from "react-select";
import CalendarIcon from "../icons/CalendarIcon";
import DownArrowIcon from "../icons/DownArrowIcon";
import { useTranslationContext } from "../context/TranslationsProvider";

const PlazoSelect = ({ plazo, setPlazo, plazosDisponibles }) => {
    const { translations } = useTranslationContext();
    const t = (key, fallback) => translations[key] || fallback;

    useEffect(() => {
        if (!plazo) return;

        if (plazosDisponibles && plazosDisponibles.length > 0) {
            const plazoNum = parseInt(plazo);
            if (!plazosDisponibles.includes(plazoNum)) {
                setPlazo("");
            }
        }
    }, [plazo, plazosDisponibles, setPlazo]);

    const isValid = plazo && plazosDisponibles && plazosDisponibles.includes(parseInt(plazo));

    const customStyles = {
        container: (provided) => ({
            ...provided,
            position: 'relative'
        }),
        control: (provided) => ({
            ...provided,
            borderRadius: '10px',
            border: isValid ? '2px solid var(--color-success)' : '1px solid var(--color-border)',
            boxShadow: 'none',
            backgroundColor: 'white',
            cursor: 'pointer',
            minHeight: '56px',
            padding: '0 16px',
            fontSize: '16px',
            transition: 'border-color 0.2s ease'
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected ? '#0862C5' : 'transparent',
            color: 'white',
            cursor: 'pointer',
            padding: '12px 16px',
            fontSize: '16px',
            borderRadius: '10px',
            '&:hover': {
                backgroundColor: '#0862C5'
            }
        }),
        valueContainer: (provided) => ({
            ...provided,
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--spacing-2)',
            padding: 0
        }),
        input: (provided) => ({
            ...provided,
            margin: 0,
            padding: 0
        }),
        dropdownIndicator: (provided) => ({
            ...provided,
            padding: 'var(--spacing-4)',
            color: 'var(--primary-neutral-600)',
            '&:hover': {
                color: 'var(--primary-neutral-900)'
            }
        }),
        indicatorSeparator: () => ({ display: 'none' }),
        menu: (provided) => ({
            ...provided,
            marginTop: '4px',
            borderRadius: '8px',
            backgroundColor: '#0E335D',
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
            padding: '8px'
        }),
        menuList: (provided) => ({
            ...provided,
            padding: 0,
            backgroundColor: '#0E335D',
            maxHeight: '200px',
            '::-webkit-scrollbar': {
                width: '8px'
            },
            '::-webkit-scrollbar-track': {
                background: '#0A2647',
                borderRadius: '4px'
            },
            '::-webkit-scrollbar-thumb': {
                background: '#1D4A80',
                borderRadius: '4px',
                ':hover': {
                    background: '#2661AB'
                }
            }
        })
    };

    const options = (Array.isArray(plazosDisponibles) ? plazosDisponibles : []).map(p => ({
        value: p.toString(),
        label: `${p} meses`
    }));

    const selectedOption = plazo ? options.find(option => option.value === plazo.toString()) : null;

    const handlePlazoChange = (selectedOption) => {
        setPlazo(selectedOption ? selectedOption.value : "");
    };

    return (
        <div className="plazo-select-container">
            <label className="plazo-label">
                {t('bdo-libranza-label-term', '¿A cuántos meses?')}
            </label>

            <Select
                value={selectedOption}
                onChange={handlePlazoChange}
                options={options}
                styles={customStyles}
                placeholder={t('bdo-libranza-placeholder-term', 'Escoge el plazo')}
                isClearable={false}
                isDisabled={options.length === 0}
                components={{
                    ValueContainer: ({ children, ...props }) => (
                        <components.ValueContainer {...props}>
                            <CalendarIcon style={{ marginRight: 8 }} />
                            {children}
                        </components.ValueContainer>
                    ),
                    DropdownIndicator: () => (
                        <div className="plazo-dropdown-icon">
                            <DownArrowIcon />
                        </div>
                    )
                }}
                noOptionsMessage={() => t('bdo-libranza-no-options', 'No hay plazos disponibles para el monto ingresado')}
            />

            <p className="plazo-helper">
                {options.length === 0
                    ? t('bdo-libranza-helper-no-terms', 'No hay plazos disponibles para este monto.')
                    : t('bdo-libranza-helper-term', 'Entre 6 y 96 meses')}
            </p>
        </div>
    );
};

export default PlazoSelect;
